/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-library-risks-control.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DialogInformationBox from '../../core/dialog-information-box/dialog-information-box';
import { GridColumnMenuActiveFilter } from '../../core/grid-filters';
import LibraryGrid from '../library-grid';
import { useGridCells, useTranslation } from 'hooks';
import { LibraryRiskFieldsExtended } from 'interfaces/library-risk.interface';
import Constants from 'utils/constants';
import { getAvailableRisks, showMaxLength } from 'utils/filter';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
// eslint-disable-next-line max-len
import { getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl } from '@queries/generated/legacy-risk-assessment.generated';

import './styles.scss';

interface IAddLibraryRisksControl {
    open: boolean;
    onClose: (action?: string) => void;
    libraryRisks: LibraryRiskFieldsExtended[];
    risks: LibraryRiskFieldsExtended[];
    currentControl: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl | null;
    onAssign: (closeModal?: boolean, selectedItems?: any[]) => void;
    showNew: boolean;
    openNew: () => void;
    onLoading: boolean;
    showAddRisks: boolean;
    showCategoryColumn?: boolean;
    isSystem: boolean;
    isStudyRisk?: boolean;
}

const AddLibraryRisksControl = ({
    open,
    onClose,
    libraryRisks,
    risks,
    currentControl,
    onAssign,
    showNew,
    openNew,
    onLoading,
    showAddRisks,
    isSystem,
    isStudyRisk,
    showCategoryColumn,
}: IAddLibraryRisksControl) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState<LibraryRiskFieldsExtended[]>([]);
    const [filteredRisks, setFilteredRisks] = useState<LibraryRiskFieldsExtended[]>([]);

    const { MaxLengthCell, YesNoCell } = useGridCells();

    const isAdding = isSystem && showAddRisks;

    const gridColumns = [
        ...(!showAddRisks || showCategoryColumn
            ? [
                  {
                      title: t('ra:category'),
                      id: 'item-category',
                      field: 'riskCategoryName',
                      width: 115,
                      cells: { data: MaxLengthCell(50) },
                  },
              ]
            : []),
        {
            title: t('ra:statement'),
            id: 'item-statement',
            field: 'statement',
            cells: { data: MaxLengthCell(150) },
        },
        {
            title: t('ra:library'),
            id: 'item-library',
            field: 'library',
            width: 105,
            hideFilter: showAddRisks,
            cells: { data: YesNoCell },
            filterUi: !showAddRisks ? GridColumnMenuActiveFilter : undefined,
        },
        ...(!showAddRisks
            ? [
                  {
                      title: t('ra:linked'),
                      id: 'item-linked',
                      field: 'isLinkIcon',
                      width: 105,
                      cells: { data: YesNoCell },
                      filterUi: GridColumnMenuActiveFilter,
                  },
              ]
            : []),
    ];

    const close = () => {
        setSelectedItems([]);
        onClose();
    };

    const addItems = (closeModal?: boolean) => {
        onAssign(closeModal, selectedItems);
    };

    useEffect(() => {
        if (!open) {
            setSelectedItems([]);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            let systemRisks = libraryRisks;
            let array: any[];
            if (
                !isAdding &&
                risks?.length &&
                libraryRisks?.length &&
                currentControl &&
                currentControl.systemReferenceId !== Constants.ObjectId.Empty
            ) {
                systemRisks = systemRisks.map((item: any) => ({
                    ...item,
                    isLinkIcon: item.libraryControlIds?.indexOf(currentControl.systemReferenceId) >= 0,
                }));
                array = getAvailableRisks(systemRisks, risks, currentControl, '', true, isSystem);
            } else {
                array = getAvailableRisks(libraryRisks, risks, currentControl, '', true, isSystem);
            }

            setFilteredRisks(array);
        }
    }, [libraryRisks, open, risks]);

    useEffect(() => {
        if (open) {
            setSelectedItems(
                selectedItems.filter((item: any) => filteredRisks.findIndex((el: any) => el.id === item.id) >= 0),
            );
        }
    }, [filteredRisks, open]);

    if (!open) return null;

    return (
        <CustomDialog
            onClose={close}
            fullWidth
            scroll="paper"
            maxWidth="md"
            open
            slotProps={{
                transition: {
                    onEntering: () => {
                        // onEntering callback is called while rendering/entering dialog
                        // check if there are risks to associate
                        // if not then open new risk dialog
                        if (
                            !getAvailableRisks(libraryRisks, risks, currentControl, '', true)?.length &&
                            open &&
                            !onLoading
                        )
                            onClose('open-add');
                    },
                },
            }}
        >
            <DialogTitle id="add-library-risk-title">
                {showAddRisks ? t('ra:addRisk(s)') : t('assignRisks')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-controls-dialog-content pb-0 mb-20">
                <div className="info-box-wrapper">
                    <DialogInformationBox
                        topText={t(
                            isStudyRisk
                                ? 'ra:addOrgRisksInformation'
                                : showAddRisks
                                  ? 'ra:addLibraryRisksInformation'
                                  : 'ra:assignLibraryRisksInformation',
                        )}
                    />
                </div>
                <>
                    {!!currentControl && (
                        <p className="description">
                            <span className="title">{t('ra:description')}: </span>
                            {showMaxLength(currentControl?.description, 600, '30vh')}
                        </p>
                    )}
                    <LibraryGrid
                        items={filteredRisks}
                        columns={gridColumns}
                        itemsType={t('ra:riskRecords')}
                        selection={{
                            selectedItems,
                            setSelectedItems,
                            showItemsCount: true,
                        }}
                    />
                </>
            </DialogContent>
            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                {showNew ? (
                    <Button disabled={onLoading} id="add-library-risk-new" text="new" onClick={openNew} />
                ) : (
                    false
                )}
                <Button
                    text={showAddRisks ? 'add' : 'assign'}
                    id="add-library-risk-save"
                    disabled={!selectedItems.length || onLoading}
                    onClick={() => addItems(false)}
                />
                <Button
                    text={showAddRisks ? 'addAndClose' : 'assignClose'}
                    id="add-library-risk-save-close"
                    disabled={!selectedItems.length || onLoading}
                    onClick={() => addItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddLibraryRisksControl;
